import React, { useRef, useState } from "react";
import { useAppContext } from "../../../../classes/contexts/AppContext";
import { useUser } from "../../../../classes/contexts/UserContext";
import { ButtonSize } from "../../../../classes/enums/ButtonEnum";
import IconsEnum from "../../../../classes/enums/IconsEnum";
import {
  formatDateWithTimeInZone,
  parseTimestampField,
} from "../../../../classes/utils/DateUtils";
import IconButton from "../../../../components/iconButton/IconButton";
import PainAssesmentModal from "../../../common/painAssesment/PainAssesmentModal";
import DeletePainAssessmentModal from "./DeletePainAssessmentModal";
import "./PainAssessmentItem.css";

// Import SVG components
import { ReactComponent as FemaleAnterior } from "../../../../assets/images/painAssesment/female_anterior.svg";
import { ReactComponent as FemalePosterior } from "../../../../assets/images/painAssesment/female_posterior.svg";
import { ReactComponent as FemaleLeft } from "../../../../assets/images/painAssesment/female_left.svg";
import { ReactComponent as FemaleRight } from "../../../../assets/images/painAssesment/female_right.svg";
import { ReactComponent as MaleAnterior } from "../../../../assets/images/painAssesment/male_anterior.svg";
import { ReactComponent as MalePosterior } from "../../../../assets/images/painAssesment/male_posterior.svg";
import { ReactComponent as MaleLeft } from "../../../../assets/images/painAssesment/male_left.svg";
import { ReactComponent as MaleRight } from "../../../../assets/images/painAssesment/male_right.svg";

const PainAssessmentItem = ({
  assessment,
  canDelete = false,
  onAssessmentDeleted,
}) => {
  const { showPopup } = useAppContext();
  const { userPreferences, dateSettings } = useUser();
  const itemRef = useRef(null);
  const [hoveredPathInfo, setHoveredPathInfo] = useState(null);
  const timeZone =
    userPreferences?.display_settings?.timezone?.toString() || "UTC";

  // Get SVG components based on gender
  const getSvgComponents = gender => {
    const components = {
      Male: [
        { component: MaleAnterior, label: "Front" },
        { component: MaleLeft, label: "Left" },
        { component: MalePosterior, label: "Back" },
        { component: MaleRight, label: "Right" },
      ],
      Female: [
        { component: FemaleAnterior, label: "Front" },
        { component: FemaleLeft, label: "Left" },
        { component: FemalePosterior, label: "Back" },
        { component: FemaleRight, label: "Right" },
      ],
    };
    return components[gender] || components.Male;
  };

  const pointsToPath = points => {
    if (!points || points.length < 2) return "";

    let path = `M ${points[0].x} ${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
      path += ` L ${points[i].x} ${points[i].y}`;
    }
    return path;
  };

  // Get paths for specific orientation
  const getPathsForOrientation = orientation => {
    return assessment.allPaths.filter(
      pathObj => pathObj.orientation === orientation
    );
  };

  const getStrokeWidth = pathObj => {
    const width = Number(pathObj?.strokeWidth);
    return width > 0 ? width : 3;
  };

  // Handle click to open edit modal
  const handleItemClick = () => {
    showPopup(<PainAssesmentModal assessment={assessment} />);
  };

  const handlePathHover = (pathObj, event) => {
    if (!pathObj || !pathObj.painLevel) return;

    const itemRect = itemRef.current?.getBoundingClientRect();
    const offsetX = (event?.clientX ?? 0) - (itemRect?.left ?? 0);
    const offsetY = (event?.clientY ?? 0) - (itemRect?.top ?? 0);

    setHoveredPathInfo({
      painLevel: pathObj.painLevel,
      painType: pathObj.painType,
      position: {
        x: itemRect ? Math.max(0, offsetX) : 0,
        y: itemRect ? Math.max(0, offsetY) : 0,
      },
    });
  };

  const handlePathLeave = () => setHoveredPathInfo(null);

  const handleDeleteClick = event => {
    event.stopPropagation();
    showPopup(
      <DeletePainAssessmentModal
        assessment={assessment}
        onAssessmentDeleted={onAssessmentDeleted}
      />
    );
  };

  // Render individual orientation with embedded paths
  const renderOrientation = (svgInfo, orientation) => {
    const { component: SvgComponent } = svgInfo;
    const pathsForOrientation = getPathsForOrientation(orientation);

    return (
      <div key={orientation} className="pain-assessment-orientation">
        <div className="svg-container">
          {/* Anatomy SVG */}
          <SvgComponent className="body-svg" />

          {/* Pain paths overlay - positioned to match exactly */}
          {pathsForOrientation.length > 0 && (
            <svg
              className="pain-paths-overlay"
              viewBox="0 0 215 584"
              preserveAspectRatio="xMidYMid meet"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                zIndex: 2,
              }}
            >
              {pathsForOrientation.map((pathObj, index) => (
                <path
                  key={`pain-path-${orientation}-${index}`}
                  d={pointsToPath(pathObj.path)}
                  stroke={pathObj.color}
                  fill="none"
                  strokeWidth={getStrokeWidth(pathObj)}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.9"
                  vectorEffect="non-scaling-stroke"
                  onMouseEnter={e => handlePathHover(pathObj, e)}
                  onMouseMove={e => handlePathHover(pathObj, e)}
                  onMouseLeave={handlePathLeave}
                  style={{ pointerEvents: "stroke", cursor: "pointer" }}
                />
              ))}
            </svg>
          )}
        </div>
      </div>
    );
  };

  const svgComponents = getSvgComponents(assessment.gender);
  const timestamp = parseTimestampField(
    assessment?.createdAt || assessment?.updatedAt
  );
  const formattedDate = timestamp
    ? formatDateWithTimeInZone(timestamp, timeZone, {
        locale: dateSettings?.locale,
      })
    : "";

  return (
    <div
      className="pain-assessment-item"
      onClick={handleItemClick}
      ref={itemRef}
      onMouseLeave={handlePathLeave}
      style={{ cursor: "pointer" }}
    >
      <div className="pain-assessment-header">
        <div className="pain-assessment-date">{formattedDate}</div>
        {canDelete && (
          <div className="pain-assessment-delete-action">
            <IconButton
              icon={IconsEnum.DELETE}
              size={ButtonSize.SMALL}
              noBgColor
              title="Delete Pain Assessment"
              onClick={handleDeleteClick}
            />
          </div>
        )}
      </div>

      <div className="pain-assessment-orientations">
        {svgComponents.map((svgInfo, orientation) =>
          renderOrientation(svgInfo, orientation)
        )}
      </div>

      {/* Pain Scale Gradient */}
      <div className="pain-scale-gradient">
        <div className="pain-scale-label">0</div>
        <div className="pain-scale-bar"></div>
        <div className="pain-scale-label">10</div>
      </div>

      {hoveredPathInfo && (
        <div
          className="pain-path-tooltip"
          style={{
            left: hoveredPathInfo.position?.x || 0,
            top: hoveredPathInfo.position?.y || 0,
          }}
        >
          <div className="pain-path-tooltip-level">
            Pain level {hoveredPathInfo.painLevel}
          </div>
          <div className="pain-path-tooltip-type">
            Type: {hoveredPathInfo.painType || "Not specified"}
          </div>
        </div>
      )}
    </div>
  );
};

export default PainAssessmentItem;
